import { useNavigate, useParams } from "react-router-dom";
import { MouseEvent, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { ConnFeatureFilter, FilterTypes, PopularityFilters } from "../../types/CDIFilter";
import FiltersDropdown from "../common/FiltersDropdown";
import FilterItem from "../common/FilterItem";
import FilterItemPopularity from "../common/FilterItemPopularity";
import { trackHubspotPageView } from "../../utils/hubspotUtils";
import {
	DESIGN_CODE_OPTIONS,
	DesignCodeOption,
	FILTER_GROUPS,
	FilterGroupConfig,
	POPULARITY_FILTER_ITEMS,
} from "../../utils/filterConfig";

interface FiltersProps {
	featureFilters: ConnFeatureFilter[];
	connectorFilters: ConnFeatureFilter[];
	popularityFilters: PopularityFilters;
	designCodeFilter: string;
	setFeatureFilters: (featureFilters: ConnFeatureFilter[]) => void;
	setConnectorFilters: (connectorFilters: ConnFeatureFilter[]) => void;
	setPopularityFilters: (popularityFilters: PopularityFilters) => void;
	setDesignCodeFilter: (value: string) => void;
	handleApplyFilters: (filter: ConnFeatureFilter, filterType: FilterTypes) => void;
	handleApplyPopularityFilter?: (filterKey: keyof PopularityFilters, clause: number) => void;
	handleSetOnlyFilter: (label: string, filterType: FilterTypes) => void;
	clearAllFilters: () => void;
	resetDesignItems: () => void;
	showPopularityFilters?: boolean;
	showDesignCodeFilter?: boolean;
	onShowSigninModal?: () => void;
}

const Filters = ({
	featureFilters,
	connectorFilters,
	popularityFilters,
	designCodeFilter,
	setDesignCodeFilter,
	handleApplyFilters,
	handleApplyPopularityFilter,
	handleSetOnlyFilter,
	clearAllFilters,
	resetDesignItems,
	showPopularityFilters = true,
	showDesignCodeFilter = true,
	onShowSigninModal,
}: FiltersProps) => {
	const navigate = useNavigate();
	const { id } = useParams();
	
	const [designCodeSelectValue, setDesignCodeSelectValue] = useState<DesignCodeOption>(
		DESIGN_CODE_OPTIONS[0],
	);
	
	// Update select value when designCodeFilter changes
	useEffect(() => {
		const selectedOption = DESIGN_CODE_OPTIONS.find(
			(option) => option.value === designCodeFilter,
		);
		if (selectedOption) {
			setDesignCodeSelectValue(selectedOption);
		}
	}, [designCodeFilter]);
	
	const onApplyFilters = (filter: ConnFeatureFilter, filterType: FilterTypes) => {
		handleApplyFilters(filter, filterType);
		resetDesignItems();
		
		const redirectUrl = `/set/${id}${window.location.search}`;
		trackHubspotPageView(redirectUrl);
	};
	
	const onSetOnlyFilter = (
		event: MouseEvent<HTMLDivElement>,
		label: string,
		filterType: FilterTypes,
	) => {
		event.stopPropagation();
		handleSetOnlyFilter(label, filterType);
		resetDesignItems();
		
		const redirectUrl = `/set/${id}${window.location.search}`;
		trackHubspotPageView(redirectUrl);
		navigate(redirectUrl);
	};
	
	const handleDesignCodeChange = (option: DesignCodeOption | null) => {
		if (option) {
			setDesignCodeSelectValue(option);
			setDesignCodeFilter(option.value);
			resetDesignItems();
		}
	};
	
	const handleClearFilters = () => {
		clearAllFilters();
		resetDesignItems();
	};
	
	const renderFilterGroup = (
		config: FilterGroupConfig,
		filters: ConnFeatureFilter[],
	) => {
		const filterType = config.filterType;
		
		return (
			<div className="filters-group" key={config.titleMessageId}>
				<h4 className="filters-group-title">
					<FormattedMessage
						id={config.titleMessageId}
						defaultMessage={config.title}
					/>
				</h4>
				{config.items.map((item) => {
					const filterValue =
						filters.find((x) => x.feature === item.feature)?.clause ?? 0;
					return (
						<FilterItem
							key={item.feature}
							name={item.name}
							defaultMessage={item.defaultMessage}
							icon={item.icon}
							label={item.feature}
							value={filterValue}
							filterType={item.filterType}
							handleSetOnlyFilter={(event) =>
								onSetOnlyFilter(event, item.feature, filterType)
							}
							onChange={onApplyFilters}
							filters={filters}
						/>
					);
				})}
			</div>
		);
	};
	
	const renderPopularityFilterGroup = () => {
		return (
			<div className="filters-group" key="popularity">
				<h4 className="filters-group-title">
					<FormattedMessage id="Popularity" defaultMessage="Popularity" />
				</h4>
				{POPULARITY_FILTER_ITEMS.map((item) => (
					<FilterItemPopularity
						key={item.feature}
						name={item.name}
						defaultMessage={item.defaultMessage}
						icon={item.icon}
						filterKey={item.feature}
						value={popularityFilters[item.feature]}
						onShowSigninModal={() => onShowSigninModal?.()}
						handleSetOnlyFilter={(event) =>
							onSetOnlyFilter(event, item.feature, "popularity")
						}
						onChange={(filterKey, clause) => {
							if (handleApplyPopularityFilter) {
								handleApplyPopularityFilter(filterKey, clause);
								resetDesignItems();
								const redirectUrl = `/set/${id}${window.location.search}`;
								trackHubspotPageView(redirectUrl);
							}
						}}
						allFilters={popularityFilters}
					/>
				))}
			</div>
		);
	};
	
	return (
		<div className="filters">
			{showDesignCodeFilter && (
				<div className="filters-group">
					<h4 className="filters-group-title">
						<FormattedMessage id="DesignCode" defaultMessage="Design code" />
					</h4>
					<FiltersDropdown
						defaultItem={designCodeSelectValue}
						items={DESIGN_CODE_OPTIONS}
						onSelect={handleDesignCodeChange}
						isFlagDropdown={true}
					/>
				</div>
			)}
			
			{renderFilterGroup(FILTER_GROUPS.connectors, connectorFilters)}
			{renderFilterGroup(FILTER_GROUPS.features, featureFilters)}
			
			{showPopularityFilters && renderPopularityFilterGroup()}
			
			<div className="filters-clear" onClick={handleClearFilters}>
				<FormattedMessage id="ClearAllFilters" defaultMessage="Clear all filters" />
			</div>
		</div>
	);
};

export default Filters;